import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { PlotterMode } from 'business/data-analysis/constants';
import { DateRangeValue } from 'technical/types';
import { NumberRangeValue } from 'ui/form/number-range-input';

import { OnChangeGraphFilters } from './types';

export type UseFilters = (
  mode: PlotterMode,
  defaultDateRange?: [Dayjs, Dayjs],
) => {
  filters: {
    dateFilters: {
      startDate: Dayjs;
      endDate: Dayjs;
    };
    ringFilters: NumberRangeValue;
  };
  onChangeFilters: {
    onChangeDateRange: (dateRange: DateRangeValue) => void;
    onChangeRingRange: OnChangeGraphFilters['onChangeRingRange'];
  };
};

export const useFilters: UseFilters = (mode, defaultDateRange) => {
  const { currentConstructionSite } = useAppContext();

  const [startDate, setStartDate] = useState<Dayjs>(
    defaultDateRange
      ? defaultDateRange[0]
      : dayjs().subtract(1, 'day').startOf('day'),
  );
  const [endDate, setEndDate] = useState<Dayjs>(
    defaultDateRange
      ? defaultDateRange[1]
      : dayjs().subtract(1, 'day').endOf('day'),
  );

  const [ringRange, setRingRange] = useState<NumberRangeValue>([null, null]);

  const onChangeDateRange = (dates: DateRangeValue) => {
    if (dates?.[0]) {
      const startInTz = dayjs(dates[0]).tz(
        currentConstructionSite?.timeZone,
        true,
      );
      setStartDate(startInTz);
    }

    if (dates?.[1]) {
      const endInTz = dayjs(dates[1]).tz(
        currentConstructionSite?.timeZone,
        true,
      );
      setEndDate(endInTz);
    }
  };

  const onChangeRingRange: OnChangeGraphFilters['onChangeRingRange'] = (
    values: NumberRangeValue,
  ) => {
    setRingRange(values);
  };

  const dateFilters = { startDate, endDate };
  const ringFilters = ringRange;

  // Reset filter on mode change
  useEffect(() => {
    if (mode === PlotterMode.RING) {
      setStartDate(
        defaultDateRange
          ? defaultDateRange[0]
          : dayjs().subtract(1, 'day').startOf('day'),
      );
      setEndDate(
        defaultDateRange
          ? defaultDateRange[1]
          : dayjs().subtract(1, 'day').endOf('day'),
      );
    } else if (PlotterMode.TIME_PERIOD) {
      setRingRange([null, null]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, defaultDateRange?.[0].format(), defaultDateRange?.[1].format()]);

  return {
    filters: { dateFilters, ringFilters },
    onChangeFilters: { onChangeDateRange, onChangeRingRange },
  };
};
